import { Button, SelectionItemId, tokens } from "@fluentui/react-components";
import { ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";
import React, { useRef } from "react";
import { ReactNode, useMemo, useState } from "react";
import { DualListBoxSelectList } from "./DualListBoxSelectList";
import styles from "./index.module.css";

export const DualListBox = <T,>({
  getKey,
  renderItem,
  itemHeight,
  sort,
  initialItems,
  items,
  visibleItems,
  onChange,
  renderHeader,
  style,
  disabled = false,
}: {
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  itemHeight: number;
  sort: (a: T, b: T) => number;
  initialItems?: { left: T[]; right: T[] };
  items: { left: T[]; right: T[] };
  visibleItems?: { left: T[]; right: T[] };
  onChange: (
    items: { left: T[]; right: T[] },
    diff: { left: T[]; right: T[] }
  ) => void;
  renderHeader?: () => { left: ReactNode; right: ReactNode };
  style?: React.CSSProperties;
  disabled?: boolean;
}) => {
  initialItems = initialItems ?? items;
  visibleItems = visibleItems ?? items;
  const listStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: "auto",
  };
  const [leftSelected, setLeftSelected] = useState<SelectionItemId[]>([]);
  const [rightSelected, setRightSelected] = useState<SelectionItemId[]>([]);
  const initialItemsKeys = useMemo(
    () => ({
      left: new Set(initialItems.left.map(getKey)),
      right: new Set(initialItems.right.map(getKey)),
    }),
    [initialItems]
  );

  const refLeftList = useRef<HTMLDivElement | HTMLUListElement>(null);
  const refRightList = useRef<HTMLDivElement | HTMLUListElement>(null);

  const visibleLeftKeys = useMemo(
    () => new Set(visibleItems.left.map(getKey)),
    [visibleItems.left, getKey]
  );
  const visibleRightKeys = useMemo(
    () => new Set(visibleItems.right.map(getKey)),
    [visibleItems.right, getKey]
  );
  const visibleLeftSelected = useMemo(
    () => leftSelected.filter((id) => visibleLeftKeys.has(`${id}`)),
    [leftSelected, visibleLeftKeys]
  );
  const visibleRightSelected = useMemo(
    () => rightSelected.filter((id) => visibleRightKeys.has(`${id}`)),
    [rightSelected, visibleRightKeys]
  );

  const onToRight = () => {
    if (visibleLeftSelected.length === 0) return;
    const selectedSet = new Set(visibleLeftSelected.map(String));
    const newLeft = items.left.filter((item) => !selectedSet.has(getKey(item)));
    const newRight = items.right.concat(
      items.left.filter((item) => selectedSet.has(getKey(item)))
    );
    const diffLeft = newLeft.filter(
      (item) => !initialItemsKeys.left.has(getKey(item))
    );
    const diffRight = newRight.filter(
      (item) => !initialItemsKeys.right.has(getKey(item))
    );
    setLeftSelected([]);
    setRightSelected([]);
    onChange(
      { left: newLeft, right: newRight },
      { left: diffLeft, right: diffRight }
    );
    refRightList.current?.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };
  const onToLeft = () => {
    if (visibleRightSelected.length === 0) return;
    const selectedSet = new Set(visibleRightSelected.map(String));
    const newRight = items.right.filter(
      (item) => !selectedSet.has(getKey(item))
    );
    const newLeft = items.left.concat(
      items.right.filter((item) => selectedSet.has(getKey(item)))
    );
    const diffLeft = newLeft.filter(
      (item) => !initialItemsKeys.left.has(getKey(item))
    );
    const diffRight = newRight.filter(
      (item) => !initialItemsKeys.right.has(getKey(item))
    );
    setLeftSelected([]);
    setRightSelected([]);
    onChange(
      { left: newLeft, right: newRight },
      { left: diffLeft, right: diffRight }
    );
    refLeftList.current?.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };
  return (
    <div className={styles.dualListBox} style={style}>
      <div className={styles.dualListBoxListWrapper}>
        {renderHeader?.().left}
        <DualListBoxSelectList
          selected={visibleLeftSelected}
          setSelected={setLeftSelected}
          initialItemKeysSet={initialItemsKeys.left}
          items={visibleItems.left}
          getKey={getKey}
          renderItem={renderItem}
          sort={sort}
          listStyle={listStyle}
          type="delete"
          itemHeight={itemHeight}
          refList={refLeftList}
        />
      </div>
      <div className={styles.dualListBoxControls}>
        <Button
          style={{ minWidth: 50 }}
          icon={<ChevronRightRegular />}
          onClick={onToRight}
          disabled={disabled}
        />
        <Button
          style={{ minWidth: 50 }}
          icon={<ChevronLeftRegular />}
          onClick={onToLeft}
          disabled={disabled}
        />
      </div>
      <div className={styles.dualListBoxListWrapper}>
        {renderHeader?.().right}
        <DualListBoxSelectList
          selected={visibleRightSelected}
          setSelected={setRightSelected}
          initialItemKeysSet={initialItemsKeys.right}
          items={visibleItems.right}
          getKey={getKey}
          renderItem={renderItem}
          sort={sort}
          listStyle={listStyle}
          type="add"
          itemHeight={itemHeight}
          refList={refRightList}
        />
      </div>
    </div>
  );
};
